import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  functionsApi,
  rolesApi,
  userAccessApi,
  type FunctionModuleView,
  type RoleView,
  type ScopeBranchView,
  type UserAccessView,
} from "../../api/access";
import { adminApi, type User } from "../../api/admin";
import type { ApiError } from "../../api/client";
import { formatApiError } from "../../lib/apiError";
import { matchesQuery } from "../../lib/foldText";
import { computeEffective, requiresMap, type EffectiveEntry } from "../../lib/effectiveAccess";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { FunctionMatrix, type UserRowState } from "../../components/FunctionMatrix";
import { StructureScopeSection, type ScopeMode } from "../../components/StructureScopeSection";
import Ic from "../../components/icons.jsx";

/**
 * `/admin/korisnici/:id/pristup` — "Pristup korisnika" (sprint 7 §5): the
 * user's roles (checkbox list) and, below, the function matrix with three
 * states per row (from role / added / removed). The effective picture is
 * recomputed locally on every change (lib/effectiveAccess, same algorithm as
 * the backend) so the screen never waits for the server while editing.
 * "Sačuvaj" sends the whole picture (PUT). Without `users:access` the screen
 * is read-only (a user looking at their own access). Above the matrix: the
 * structure scope (§12) — where the structure and sastavnica functions apply.
 */
export default function AdminUserAccessPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { state, hasPermission } = useAuth();
  const canAccess = hasPermission("users:access");
  const self = state.status === "authenticated" && state.user.id === id;
  const readOnly = !canAccess;

  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<RoleView[] | null>(null);
  const [modules, setModules] = useState<FunctionModuleView[] | null>(null);
  const [access, setAccess] = useState<UserAccessView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Draft — the whole picture, sent as one PUT.
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set());
  const [grantKeys, setGrantKeys] = useState<Set<string>>(new Set());
  const [denyKeys, setDenyKeys] = useState<Set<string>>(new Set());
  const [scopeMode, setScopeMode] = useState<ScopeMode>("all");
  const [scopeBranches, setScopeBranches] = useState<ScopeBranchView[]>([]);
  const [roleQuery, setRoleQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const applyAccess = useCallback((a: UserAccessView) => {
    setAccess(a);
    setRoleIds(new Set(a.roles.map((r) => r.id)));
    setGrantKeys(new Set(a.grantKeys));
    setDenyKeys(new Set(a.denyKeys));
    setScopeBranches(a.structureScope);
    setScopeMode(a.structureScope.length > 0 ? "branches" : "all");
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoadError(null);
    const fail = (err: unknown) => { if (!cancelled) setLoadError(formatApiError(err as ApiError)); };
    adminApi.getUser(id).then((u) => { if (!cancelled) setUser(u); }).catch(fail);
    userAccessApi.get(id).then((a) => { if (!cancelled) applyAccess(a); }).catch(fail);
    // The catalogue and the role list need `roles:view` / `users:access`; a user viewing
    // their own access may lack both — then the screen falls back to the server's picture.
    rolesApi.list().then((r) => { if (!cancelled) setRoles(r); }).catch(() => { if (!cancelled) setRoles([]); });
    functionsApi.catalogue().then((m) => { if (!cancelled) setModules(m); }).catch(() => { if (!cancelled) setModules([]); });
    return () => { cancelled = true; };
  }, [id, applyAccess]);

  const allKeys = useMemo(() => modules?.flatMap((m) => m.functions.map((f) => f.key)) ?? [], [modules]);
  const requires = useMemo(() => requiresMap(modules ?? []), [modules]);

  // Live effective picture from the draft (§3). When the role catalogue is unavailable
  // (read-only self view) the server's `effective` list is shown as is.
  const effective = useMemo<Map<string, EffectiveEntry>>(() => {
    if (roles && roles.length > 0) {
      const chosen = roles.filter((r) => roleIds.has(r.id)).map((r) => ({ id: r.id, name: r.name, allFunctions: r.allFunctions, functionKeys: r.functionKeys }));
      return new Map(computeEffective({ allKeys, roles: chosen, grantKeys, denyKeys, requires }).map((e) => [e.key, e]));
    }
    return new Map((access?.effective ?? []).map((e) => [e.key, e]));
  }, [roles, roleIds, allKeys, grantKeys, denyKeys, requires, access]);

  const grantedCount = useMemo(() => [...effective.values()].filter((e) => e.granted).length, [effective]);

  // What the PUT sends: [] = the whole structure. "Samo odabrane grane" without one cannot be saved.
  const scopeIds = useMemo(() => (scopeMode === "all" ? [] : scopeBranches.map((b) => b.id)), [scopeMode, scopeBranches]);
  const scopeIncomplete = scopeMode === "branches" && scopeBranches.length === 0;
  const scopedFunctionNames = useMemo(
    () => (modules ?? []).flatMap((m) => m.functions.filter((f) => f.structureScoped).map((f) => f.name)),
    [modules],
  );

  const dirty = useMemo(() => {
    if (!access) return false;
    const same = (a: Set<string>, b: string[]) => a.size === b.length && b.every((k) => a.has(k));
    return !(same(roleIds, access.roles.map((r) => r.id)) && same(grantKeys, access.grantKeys) && same(denyKeys, access.denyKeys)
      && same(new Set(scopeIds), access.structureScope.map((b) => b.id)));
  }, [access, roleIds, grantKeys, denyKeys, scopeIds]);

  function toggleRole(roleId: string) {
    setRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
      return next;
    });
  }

  function setRowState(keys: string[], s: UserRowState) {
    setGrantKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) { if (s === "GRANT") next.add(k); else next.delete(k); }
      return next;
    });
    setDenyKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) { if (s === "DENY") next.add(k); else next.delete(k); }
      return next;
    });
  }

  async function handleSave() {
    if (!id) return;
    setSaving(true);
    try {
      const saved = await userAccessApi.update(id, {
        roleIds: [...roleIds], grantKeys: [...grantKeys], denyKeys: [...denyKeys], structureScope: scopeIds,
      });
      applyAccess(saved);
      toast.success(t("userAccess.toast.saved", "Pristup sačuvan."));
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "SELF_LOCKOUT") toast.error(t("userAccess.error.selfLockout", "Ne možete sebi oduzeti dodelu pristupa."));
      else if (ae.code === "LAST_ADMIN") toast.error(t("userAccess.error.lastAdmin", "Bar jedan aktivan korisnik mora zadržati dodelu pristupa i izmenu uloga."));
      else toast.error(formatApiError(ae));
    } finally {
      setSaving(false);
    }
  }

  if (state.status === "loading") return null;
  // Own row is readable by anyone (§1); anyone else's needs users:view (users:access implies it) — read-only without users:access.
  if (!self && !hasPermission("users:view")) return <Navigate to="/forbidden" replace />;

  const visibleRoles = (roles ?? []).filter((r) =>
    matchesQuery(`${r.name} ${r.code} ${r.description ?? ""}`, roleQuery));
  const ready = user !== null && access !== null && roles !== null && modules !== null;
  const title = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : t("userAccess.title", "Pristup korisnika");

  return (
    <div>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span><Link to="/admin/korisnici">{t("admin.users.title", "Korisnici")}</Link></span>
            <span>{t("userAccess.crumb", "Pristup")}</span>
          </nav>
          <h1>{title}</h1>
          <div className="sub">
            {t("userAccess.title", "Pristup korisnika")}{user && <> · {user.email}</>}
            {self && <> · {t("userAccess.self", "vaš nalog")}</>}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => navigate(self && !canAccess ? "/profil" : "/admin/korisnici")}>
            {t("action.back", "Nazad")}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn primary"
              disabled={!dirty || saving || !ready || scopeIncomplete}
              title={scopeIncomplete ? t("userAccess.scope.none", "Izaberite bar jednu granu — bez grana korisnik menja celu strukturu.") : undefined}
              onClick={() => { void handleSave(); }}
            >
              {saving ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
            </button>
          )}
        </div>
      </div>

      {loadError && <div style={errorBox} role="alert">{loadError}</div>}
      {!ready && !loadError && <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>}

      {ready && (
        <div className="split-layout">
          <aside className="card split-side">
            <div className="card-h">
              <div>
                <h3>{t("userAccess.rolesSection", "Uloge")}</h3>
                <div className="sub">{t("userAccess.rolesSub", "Korisnik dobija funkcije svake izabrane uloge.")}</div>
              </div>
            </div>
            <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {roles.length === 0 ? (
                <div className="role-list">
                  {access.roles.length === 0 && <div className="empty" style={{ padding: 16 }}>{t("userAccess.noRoles", "Bez uloga.")}</div>}
                  {access.roles.map((r) => (
                    <div key={r.id} className="check-row">
                      <div className="role-list-text"><b>{r.name}</b><span className="code">{r.code}</span></div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="search-wrap" style={{ position: "relative" }}>
                    <Ic.search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "var(--ink-3)", pointerEvents: "none" }} />
                    <input
                      className="input"
                      style={{ paddingLeft: 32 }}
                      value={roleQuery}
                      onChange={(e) => setRoleQuery(e.target.value)}
                      placeholder={t("userAccess.roleSearch", "Pretraga uloga…")}
                      aria-label={t("userAccess.roleSearch", "Pretraga uloga…")}
                    />
                  </div>
                  <div className="role-list">
                    {roles.map((r) => (
                      <label key={r.id} className="check-row" hidden={!visibleRoles.includes(r)}>
                        <input type="checkbox" checked={roleIds.has(r.id)} disabled={readOnly} onChange={() => toggleRole(r.id)} aria-label={r.name} />
                        <span className="role-list-text">
                          <b>{r.name}</b>
                          {r.description && <span className="sub">{r.description}</span>}
                          <span className="code">{r.code}</span>
                        </span>
                        <span className="role-count">
                          {r.allFunctions
                            ? t("roles.allFunctionsShort", "Sve")
                            : t("userAccess.roleFunctions", "{{count}} f.", { count: r.functionKeys.length + r.impliedKeys.length })}
                        </span>
                      </label>
                    ))}
                    <div className="empty" style={{ padding: 16 }} hidden={visibleRoles.length > 0}>
                      {t("userAccess.noRoleMatch", "Nijedna uloga ne odgovara pretrazi.")}
                    </div>
                  </div>
                </>
              )}
              <div className="muted" style={{ fontSize: 12.5 }}>
                {t("userAccess.summary", "Efektivno: {{granted}} od {{total}} funkcija · dodato {{grants}} · oduzeto {{denies}}", {
                  granted: grantedCount, total: allKeys.length || effective.size, grants: grantKeys.size, denies: denyKeys.size,
                })}
              </div>
              {readOnly && (
                <div className="muted" style={{ fontSize: 12.5 }}>
                  {t("userAccess.readOnly", "Samo pregled — za izmenu je potrebna funkcija „Dodela uloga i funkcija“.")}
                </div>
              )}
            </div>
          </aside>

          <div style={{ minWidth: 0 }}>
            <StructureScopeSection
              mode={scopeMode}
              branches={scopeBranches}
              readOnly={readOnly}
              scopedFunctions={scopedFunctionNames}
              onModeChange={setScopeMode}
              onAdd={(b) => setScopeBranches((prev) => [...prev, b])}
              onRemove={(branchId) => setScopeBranches((prev) => prev.filter((b) => b.id !== branchId))}
            />
            <div className="section-h">
              <h3>{t("userAccess.functionsSection", "Funkcije")}</h3>
              <span className="sub">{t("userAccess.functionsSub", "„Iz uloge“ prati uloge; „Dodato“ / „Oduzeto“ su izuzeci samo za ovog korisnika. Oduzeto uvek pobeđuje.")}</span>
            </div>
            {modules.length === 0 ? (
              <div className="card">
                <div className="card-b" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[...effective.values()].filter((e) => e.granted).map((e) => (
                    <code key={e.key} className="tag ok" style={{ fontFamily: "var(--mono)" }}>{e.key}</code>
                  ))}
                  {grantedCount === 0 && <span className="muted">{t("userAccess.noFunctions", "Bez funkcija.")}</span>}
                </div>
              </div>
            ) : (
              <FunctionMatrix
                mode="user"
                modules={modules}
                grantKeys={grantKeys}
                denyKeys={denyKeys}
                effective={effective}
                onChange={setRowState}
                disabled={readOnly}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
